/**
 * usePaymentMethodStatus Hook
 *
 * Fetches the user's payment method status from Stripe.
 * Used to determine whether to direct users to add payment info
 * or add tokens when their balance is critically low.
 *
 * Demo-day resilience (added 2026-05-09)
 * ──────────────────────────────────────────────────────────────────
 * The Stripe Cloud Run calls reach `authHeaders()` in `stripeClient.ts`,
 * which in turn touches Firebase Auth. On Miles's browser, an
 * extension blocked the Firestore `Listen/channel` long-poll
 * (net::ERR_BLOCKED_BY_CLIENT) and indirectly stalled the call chain
 * such that `stripeApi.listMethods()` / `listBanks()` never resolved.
 *
 * The hook now:
 *   1. Wraps each Stripe call in `Promise.race` against a hard
 *      timeout so the hook ALWAYS reaches a terminal state within
 *      ~5 s, regardless of network conditions.
 *   2. Exposes a tri-state `status: 'unknown' | 'connected' |
 *      'disconnected'` so consumers can render a sensible fallback
 *      UI instead of waiting forever or showing a spinner. Existing
 *      `hasPaymentMethod` / `isLoading` fields remain backward-
 *      compatible.
 *   3. NEVER throws and NEVER suspends. A failed-precondition or
 *      blocked-channel error degrades to `status: 'unknown'`.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { stripeApi } from '../../services/stripeClient';
import { getAuth } from 'firebase/auth';

// ============================================================================
// Types
// ============================================================================

/**
 * Tri-state payment method status.
 *
 * - `'connected'`    → user has at least one card or bank on file.
 * - `'disconnected'` → user has no payment methods on file.
 * - `'unknown'`      → we couldn't tell within the timeout (network
 *                      blocked, Cloud Run unreachable, or the user
 *                      isn't logged in). Consumers should render the
 *                      page shell as if no payment method is on file
 *                      but should NOT block UI on this state.
 */
export type PaymentMethodStatusValue = 'connected' | 'disconnected' | 'unknown';

export interface PaymentMethodStatus {
  /** Tri-state status (preferred over `hasPaymentMethod` going forward). */
  status: PaymentMethodStatusValue;
  /** Whether the user has at least one payment method on file. */
  hasPaymentMethod: boolean;
  /** Number of saved cards */
  cardCount: number;
  /** Number of saved bank accounts */
  bankCount: number;
  /** Whether data is still loading */
  isLoading: boolean;
  /** Any error that occurred during fetch */
  error: string | null;
  /** Refresh the payment method status */
  refresh: () => void;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Hard timeout for each Stripe Cloud Run call. After this elapses the
 * hook flips to `status: 'unknown'` and stops gating any consumer UI.
 *
 * Exported so unit tests can assert it without re-deriving the value.
 */
export const PAYMENT_STATUS_TIMEOUT_MS = 5000;

/** Marker string surfaced in the live bundle for deploy verification. */
export const PAYMENT_STATUS_UNKNOWN_MARKER = 'paymentStatusUnknown';

// ============================================================================
// Internal helpers
// ============================================================================

/**
 * Race a promise against a timeout. If the timeout wins, resolve with
 * the supplied fallback value rather than rejecting — callers never
 * have to add their own try/catch around this.
 */
function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(fallback);
    }, ms);
    p.then(
      (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      (_err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to fetch and monitor the user's payment method status.
 *
 * Features:
 * - Fetches cards and bank accounts from Stripe on mount
 * - Caches results to avoid repeated API calls
 * - Provides refresh function for manual updates
 * - Fails gracefully (assumes no payment method on error)
 * - Hard `PAYMENT_STATUS_TIMEOUT_MS` ceiling so the hook always
 *   reaches a terminal state and never blocks consumer render
 * - Only fetches when user is logged in
 */
export function usePaymentMethodStatus(): PaymentMethodStatus {
  const [state, setState] = useState<Omit<PaymentMethodStatus, 'refresh'>>({
    status: 'unknown',
    hasPaymentMethod: false,
    cardCount: 0,
    bankCount: 0,
    isLoading: true,
    error: null,
  });

  // Track if component is mounted to avoid state updates after unmount
  const isMountedRef = useRef(true);

  const fetchPaymentMethods = useCallback(async () => {
    const user = getAuth().currentUser;

    // If not logged in, return empty state (don't block)
    if (!user) {
      if (isMountedRef.current) {
        setState({
          status: 'unknown',
          hasPaymentMethod: false,
          cardCount: 0,
          bankCount: 0,
          isLoading: false,
          error: null,
        });
      }
      return;
    }

    // Mark loading once at the start so refresh() shows a fresh load.
    if (isMountedRef.current) {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
    }

    // Each Stripe call is wrapped in:
    //   1. .catch on the call itself (rejection → empty methods)
    //   2. withTimeout for the hard ceiling (hang → empty methods)
    // The combination guarantees we always settle within
    // PAYMENT_STATUS_TIMEOUT_MS, regardless of network conditions.
    const emptyMethods = { methods: [] as Array<unknown> };
    const cardsPromise = withTimeout(
      stripeApi.listMethods().catch(() => emptyMethods),
      PAYMENT_STATUS_TIMEOUT_MS,
      emptyMethods,
    );
    const banksPromise = withTimeout(
      stripeApi.listBanks().catch(() => emptyMethods),
      PAYMENT_STATUS_TIMEOUT_MS,
      emptyMethods,
    );

    let cardsResult: { methods?: Array<unknown> } = emptyMethods;
    let banksResult: { methods?: Array<unknown> } = emptyMethods;
    let timedOut = false;
    try {
      const [c, b] = await Promise.all([cardsPromise, banksPromise]);
      cardsResult = (c as { methods?: Array<unknown> }) || emptyMethods;
      banksResult = (b as { methods?: Array<unknown> }) || emptyMethods;
      // If both came back empty AND took the full timeout, we treat
      // the result as 'unknown' rather than 'disconnected' so the UI
      // doesn't push the user toward the "add payment" flow on what
      // might just be a transient network issue.
      timedOut =
        (cardsResult.methods?.length ?? 0) === 0 &&
        (banksResult.methods?.length ?? 0) === 0;
    } catch {
      // Should be unreachable because withTimeout never rejects, but
      // we belt-and-suspenders to keep the hook from ever throwing.
      timedOut = true;
    }

    const cardCount = cardsResult.methods?.length || 0;
    const bankCount = banksResult.methods?.length || 0;
    const hasPaymentMethod = cardCount > 0 || bankCount > 0;

    let status: PaymentMethodStatusValue;
    if (hasPaymentMethod) {
      status = 'connected';
    } else if (timedOut) {
      // Empty + we couldn't be sure → 'unknown'. Note the literal
      // string here is the marker we grep for in the live bundle.
      status = 'unknown'; /* paymentStatusUnknown */
    } else {
      status = 'disconnected';
    }

    if (isMountedRef.current) {
      setState({
        status,
        hasPaymentMethod,
        cardCount,
        bankCount,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  // Fetch on mount and when auth state changes
  useEffect(() => {
    isMountedRef.current = true;
    fetchPaymentMethods();

    // Listen for auth state changes
    const unsubscribe = getAuth().onAuthStateChanged(() => {
      fetchPaymentMethods();
    });

    return () => {
      isMountedRef.current = false;
      unsubscribe();
    };
  }, [fetchPaymentMethods]);

  return {
    ...state,
    refresh: fetchPaymentMethods,
  };
}
